/**
 * Router — react-router-dom v7 data router.
 *
 * Route map sourced from TASK-168 D.1 instructions. Studio routes are
 * wrapped in GuardedRoute; anonymous users are redirected to
 * `/auth/login?returnTo=…`. Wave 2 will add the 301 alias redirects
 * documented in console-architecture.md §3.
 */

import { createBrowserRouter, createMemoryRouter } from "react-router-dom";
import type { RouteObject } from "react-router-dom";

import RootLayout from "@/layout/RootLayout";
import RehearsalLayout from "@/layout/RehearsalLayout";

import MarketingHero from "@/pages/MarketingHero";
import Rehearsal from "@/pages/Rehearsal";
import NotFound from "@/pages/NotFound";

/**
 * The documentation routes are split out of the entry chunk.
 *
 * `Notes` globs 43 MDX files with `eager: true`, which lands every one of them
 * in whichever chunk imports it -- 264 KB of documentation downloaded by
 * anyone who opens the demo. The demo is a link on a resume and a visitor gets
 * about ninety seconds (`docs/02-specs/demo-acceptance.md`, beat 0).
 *
 * React Router v7's `lazy` is used rather than `React.lazy`: the data router
 * resolves it during navigation, so no Suspense boundary is needed.
 *
 * The remaining routes are still eager. Splitting them is the rest of
 * animachord#54 and wants its own pass -- it changes when transient states
 * like a loading spinner are observable, which several tests assert on.
 */
const lazyPage = (load: () => Promise<{ default: React.ComponentType }>) =>
  async () => ({ Component: (await load()).default });

/**
 * Rendered while the router resolves a lazy route on first load.
 *
 * Required by react-router v7 the moment any route in the tree is lazy:
 * without it the data router logs "No `HydrateFallback` element provided"
 * and renders nothing until hydration finishes.
 *
 * Measured against the built app before choosing what it should be: on
 * `/rehearsal` -- a route that is *not* lazy -- `#root` and the page's own
 * testid both appear in the same 25ms sample, so there is no observable
 * blank frame to fill and nothing here is normally seen. It is deliberately
 * empty rather than a spinner for that reason: a spinner that only ever
 * appears on a slow connection, for one frame, is a flash, and the visitor
 * route gets about ninety seconds of a stranger's attention
 * (`docs/02-specs/demo-acceptance.md`, beat 0).
 *
 * The `div` is not decoration. An empty fragment leaves the test renderer
 * with nothing to attach to, and the router needs an element.
 */
const HydrateFallback = () => <div data-testid="router-hydrating" />;

export const routes: RouteObject[] = [
  {
    path: "/",
    Component: RootLayout,
    HydrateFallback,
    children: [
      { index: true, Component: MarketingHero },

      // Rehearsal — anonymous, ephemeral.
      {
        path: "rehearsal",
        Component: RehearsalLayout,
        children: [
          { index: true, Component: Rehearsal },
          /*
            The developer stage keeps its score editor, cue rail and take
            log, at its own path. A visitor gets a conversation; a
            developer scoring an ensemble wants the instrument, and #66 is
            explicit that this is not a deletion.

            Before `:sessionId`, or "console" would be read as one.
          */
          {
            path: "console",
            lazy: lazyPage(() => import("@/pages/RehearsalConsole")),
          },
          { path: ":sessionId", Component: Rehearsal },
        ],
      },

      // Auth — public.
      { path: "auth/login", lazy: lazyPage(() => import("@/pages/AuthLogin")) },
      {
        path: "auth/callback",
        lazy: lazyPage(() => import("@/pages/AuthCallback")),
      },

      // Studio — guarded.
      {
        path: "studio",
        lazy: lazyPage(() => import("@/layout/GuardedRoute")),
        children: [
          {
            lazy: lazyPage(() => import("@/layout/StudioLayout")),
            children: [
              { index: true, lazy: lazyPage(() => import("@/pages/Studio")) },
              {
                path: "_admin/grants",
                lazy: lazyPage(() => import("@/pages/AdminGrants")),
              },
              {
                path: ":workspaceSlug",
                children: [
                  { index: true, lazy: lazyPage(() => import("@/pages/Studio")) },
                  {
                    path: "scores",
                    lazy: lazyPage(() => import("@/pages/Scores")),
                  },
                  {
                    path: "scores/:scoreId",
                    lazy: lazyPage(() => import("@/pages/ScoreDetail")),
                  },
                  {
                    path: "scores/:scoreId/takes/:takeId",
                    lazy: lazyPage(() => import("@/pages/TakeDetail")),
                  },
                  {
                    path: "ledger",
                    lazy: lazyPage(() => import("@/pages/Ledger")),
                  },
                  {
                    path: "tokens",
                    lazy: lazyPage(() => import("@/pages/Tokens")),
                  },
                ],
              },
            ],
          },
        ],
      },

      // Docs — public, runnable in wave 2.
      { path: "docs/*", lazy: lazyPage(() => import("@/pages/Docs")) },

      // Notes — Track G surface (Fumadocs + Pagefind + MDX). PLAN.md
      // I.3.2 redirects /docs → /notes server-side; client-side both
      // routes resolve.
      { path: "notes", lazy: lazyPage(() => import("@/pages/Notes")) },
      { path: "notes/*", lazy: lazyPage(() => import("@/pages/Notes")) },

      { path: "*", Component: NotFound },
    ],
  },
];

export function createAppRouter(): ReturnType<typeof createBrowserRouter> {
  return createBrowserRouter(routes);
}

export function createTestRouter(initialEntries: string[]): ReturnType<typeof createMemoryRouter> {
  return createMemoryRouter(routes, { initialEntries });
}
